import * as React from "react";
import {
    CommonStringField,
    EventId,
    RaceNumberField,
    RegistrationField,
} from "../../domain/event";
import { Autocomplete } from "../form/Autocomplete";
import { Input } from "../form/Input";
import { InputProps } from "../form/form";
import { useTranslation } from "../../utils/i18n/lang";
import {
    raceNumberCodec,
    raceNumberOrd,
    resultMessages,
} from "../../domain/result";
import { array, option, string } from "fp-ts";
import { useQuery } from "../../utils/supabase/useQuery";
import { identity, pipe } from "fp-ts/function";
import * as t from "io-ts";
import {
    isCompletedState,
    queryStateData,
} from "../../utils/supabase/QueryState";
import { useAlertOnQueryError } from "../../hooks/useAlertOnQueryError";
import { emptyArray } from "../../utils/fp";
import { Messages } from "../../utils/i18n/i18n";

interface Props {
    field: RegistrationField;
    formProps: InputProps;
    eventId: EventId;
}

export const RegistrationDynamicField = (p: Props) => {
    switch (p.field.kind) {
        case "raceNumber":
            return (
                <RaceNumber
                    field={p.field}
                    formProps={p.formProps}
                    eventId={p.eventId}
                />
            );
        case "commonString":
            return (
                <Input {...p.formProps} label={p.field.label} fullWidth />
            );
    }
};

// Compile the admin-provided pattern as an anchored full-match (HTML `pattern`
// semantics). A bad pattern (should be rejected by the editor) degrades to "no
// constraint" rather than crashing the racer's form.
const compilePattern = (pattern: string | undefined): RegExp | null => {
    if (pattern == null || pattern.length === 0) {
        return null;
    }
    try {
        return new RegExp(`^(?:${pattern})$`);
    } catch {
        return null;
    }
};

const commonStringAnswerCodec = (
    field: CommonStringField
): t.Type<string, string, unknown> => {
    const re = compilePattern(field.regexpPattern);
    // empty is treated as "no answer" and governed by the field's `required`
    // rule (react-hook-form), so the regexp only applies to non-empty input
    const is = (u: unknown): u is string =>
        typeof u === "string" &&
        (u.length === 0 || re == null || re.test(u));

    return new t.Type<string, string, unknown>(
        `commonStringAnswer`,
        is,
        (u, c) => (is(u) ? t.success(u) : t.failure(u, c)),
        t.identity
    );
};

const invalidFormatMessages: Messages<() => string> = {
    en: () => "Invalid format",
    cs: () => "Neplatný formát",
};

// InputDef (codec + per-lang validation message) for a commonString field,
// consumed when building the registration form definition.
export const commonStringInputDef = (field: CommonStringField) => ({
    codec: commonStringAnswerCodec(field),
    messages: invalidFormatMessages,
});

interface RaceNumberProps {
    field: RaceNumberField;
    formProps: InputProps;
    eventId: EventId;
}

export const RaceNumber = (p: RaceNumberProps) => {
    const resultM = useTranslation(resultMessages);

    const availableNumbersQuery = useQuery(
        `enumItems_${p.eventId}_${p.field.enum_id}`,
        (supabase) =>
            supabase.rpc("race_numbers_available_for_event", {
                p_event_id: p.eventId,
                p_enum_id: p.field.enum_id,
            }),
        t.array(t.type({ title: t.string }))
    );

    useAlertOnQueryError(availableNumbersQuery.state);

    const options = pipe(
        availableNumbersQuery.state,
        queryStateData,
        option.map(
            array.filterMap((item) =>
                pipe(item.title, raceNumberCodec.decode, option.fromEither)
            )
        ),
        option.getOrElse(emptyArray()),
        array.sort(raceNumberOrd)
    );

    return (
        <Autocomplete
            // race-number options are number | string; the spread keeps the
            // historical loose typing for this pre-existing field
            {...(p.formProps as any)}
            loading={!isCompletedState(availableNumbersQuery.state)}
            label={resultM.number}
            options={options}
            optionEq={string.Eq}
            getOptionLabel={identity}
        />
    );
};
